/**
 * CachedImage Component
 * Optimized image component that tracks loaded images to prevent
 * re-requests during scroll and supports offline viewing.
 * 
 * Uses browser's native caching with proper loading strategies.
 */

import { useState, useCallback, memo } from 'react';

// Global set to track loaded image URLs (in-memory cache for session)
const loadedImages = new Set<string>();

interface CachedImageProps {
  src: string;
  alt: string;
  className?: string;
  loading?: 'lazy' | 'eager';
  onLoad?: () => void;
  onError?: () => void;
  onClick?: () => void;
  draggable?: boolean;
  fallbackSrc?: string;
}

/**
 * Check if an image URL has been loaded this session
 */
export function isImageLoaded(url: string): boolean {
  return loadedImages.has(url);
}

/**
 * Mark an image as loaded (for preloading scenarios)
 */
export function markImageLoaded(url: string): void {
  loadedImages.add(url);
}

/**
 * Preload an image and mark it as loaded
 */
export function preloadImage(url: string): Promise<void> {
  if (loadedImages.has(url)) {
    return Promise.resolve();
  }
  
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      loadedImages.add(url);
      resolve();
    };
    img.onerror = reject;
    img.src = url;
  });
}

/**
 * Preload multiple images in parallel
 */
export function preloadImages(urls: string[]): Promise<void[]> {
  return Promise.all(urls.map(url => preloadImage(url).catch(() => {})));
}

export const CachedImage = memo(function CachedImage({
  src,
  alt,
  className = '',
  loading = 'lazy',
  onLoad,
  onError,
  onClick,
  draggable = false,
  fallbackSrc = '/placeholder.svg',
}: CachedImageProps) {
  const [hasError, setHasError] = useState(false);
  const [isLoaded, setIsLoaded] = useState(() => loadedImages.has(src));

  const handleLoad = useCallback(() => {
    loadedImages.add(src);
    setIsLoaded(true);
    onLoad?.();
  }, [src, onLoad]);

  const handleError = useCallback(() => {
    setHasError(true);
    onError?.();
  }, [onError]);

  const imageSrc = hasError ? fallbackSrc : src;
  
  // If already loaded, use eager loading to avoid layout shift
  const effectiveLoading = isLoaded ? 'eager' : loading;

  return (
    <img
      src={imageSrc}
      alt={alt}
      className={className}
      loading={effectiveLoading}
      decoding="async"
      draggable={draggable}
      onLoad={handleLoad}
      onError={handleError}
      onClick={onClick}
    />
  );
});

export default CachedImage;
